import {
  Button,
  FormControlLabel,
  ButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Menu,
  CircularProgress,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Fab,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './EditLines.css'
import { classType, editingLineItemType, lineItemType, orderType, shipToType } from '../types'
import { productTypes } from '../lists'
import { CurrencyInput, DatePicker, Expandable, NetsuiteSearch, NumberField } from '../helper'
import { Close, ExpandMore, ExpandLess, MoreVert, SaveOutlined, RefreshOutlined, Add } from '@mui/icons-material'
import axios from 'axios'
import ActionBar from '../Misc/ActionBar'
import { v4 as uuidv4 } from 'uuid'
import { fieldLengths } from '../fieldLengths'
import { useClassList } from '../hooks'
import {
  addBlankLineAfter,
  addSelectedLinesToKit,
  bulkApplyShippingAddress,
  bulkEdit,
  updatePricing,
  createBlankEditingLine,
  createNewKitWithSelection,
  deleteKit,
  deleteLineItem,
  deleteSelectedLines,
  duplicateLineItem,
  lineKey,
  mergeBundleIntoLines,
  flattenLineItems,
  unkitLines,
  zeroKitLinePrices,
} from './EditLinesHelper'
import dayjs from 'dayjs'

const COLUMN_WIDTHS = {
  Number: '120px',
  More: '72px',
  Supplier: '250px',
  NsVendor: '250px',
  DisplayPartNumber: '250px',
  MfrPartNumber: '175px',
  Manufacturer: '250px',
  SupplierPartNumber: '250px',
  Description: '250px',
  ShipToAddress: '250px',
  Qty: '150px',
  UnitCost: '150px',
  ExtCost: '150px',
  UnitPrice: '150px',
  ExtPrice: '150px',
  TAACompliant: '250px',
  ProductCategory: '250px',
  CountryOfOrigin: '250px',
  PoPStart: '250px',
  PoPEnd: '250px',
  ServiceTag: '250px',
  ContractNumber: '250px',
}

export default function EditLines({
  shippingAddresses,
  flatLineItems,
  setLineItems,
  setOpen,
  open,
  quoteNumber,
  orderID,
  user,
  originalFlatLineItems,
  onReset,
  order,
  setOrder,
  cqBackup,
}: {
  shippingAddresses: (shipToType & { default: boolean; id: number })[] | undefined
  flatLineItems: lineItemType[]
  setLineItems: React.Dispatch<React.SetStateAction<lineItemType[]>>
  setOpen: (open: boolean) => void
  open: boolean
  quoteNumber: string
  orderID: string
  user: { email: string }
  originalFlatLineItems: lineItemType[]
  onReset: () => void
  order: orderType
  setOrder: React.Dispatch<React.SetStateAction<orderType>>
  cqBackup: Date
}) {
  const classList = useClassList()
  const contractClassOptions = useMemo((): classType[] => {
    const parent = classList.find(cls => cls.name === order.PurchaseVehicle)
    if (!parent) return []
    return parent.children?.length ? [...(parent.availableonheader ? [parent] : []), ...parent.children] : [parent]
  }, [classList, order.PurchaseVehicle])

  const lastCheckedIndexRef = useRef<number | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | { lineItem: editingLineItemType; anchor: HTMLElement }>(null)
  const [kitHeaderAnchorEl, setKitHeaderAnchorEl] = useState<null | {
    kit: string
    anchor: HTMLElement
    lineKey: string
  }>(null)
  const [editingLineItems, setEditingLineItems] = useState<editingLineItemType[]>([])
  const [vitPrices, setVitPrices] = useState(false)
  const defaultAddress = useMemo(() => shippingAddresses?.find(address => address.default), [shippingAddresses])
  const [kitCollapsed, setKitCollapsed] = useState<string[]>([])
  const [bulkExpand, setBulkExpand] = useState<boolean>(false)
  const [saveStatus, setSaveStatus] = useState<'' | 'unsaved' | 'saving' | 'saved' | 'error'>('')
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<
    null | { type: 'line'; lineItem: (typeof editingLineItems)[0] } | { type: 'kit'; kit: string }
  >(null)
  const [unkitStatus, setUnkitStatus] = useState<'' | 'saved'>('')
  const [lineItemsLoading, setLineItemsLoading] = useState<'loading' | 'error' | 'saved' | ''>('')
  const [contraqerLineItemsDialogOpen, setContraqerLineItemsDialogOpen] = useState(false)

  const handleDuplicateLineItem = (lineItem: (typeof editingLineItems)[0]) => {
    setEditingLineItems(prev => duplicateLineItem(prev, lineItem))
    setMenuAnchorEl(null)
    setSaveStatus('unsaved')
  }

  const handleAddLineAfter = (lineItem: (typeof editingLineItems)[0]) => {
    setEditingLineItems(prev => {
      const newLine = createBlankEditingLine(lineItem.Kit ?? null, 0, null, null, null, null, null, null)
      return addBlankLineAfter(prev, lineItem, newLine)
    })
    setMenuAnchorEl(null)
    setSaveStatus('unsaved')
  }

  const handleDeleteLineItem = (lineItem: (typeof editingLineItems)[0]) => {
    setEditingLineItems(prev => deleteLineItem(prev, lineItem))
    setMenuAnchorEl(null)
    setSaveStatus('unsaved')
  }

  const handleDeleteKit = (kit: string) => {
    setEditingLineItems(prev => deleteKit(prev, kit))
    setKitHeaderAnchorEl(null)
    setSaveStatus('unsaved')
  }

  const handleCreateBundle = (mergedLine: lineItemType) => {
    setEditingLineItems(prev => mergeBundleIntoLines(prev, mergedLine))
    setSaveStatus('unsaved')
  }

  const handleDeleteSelected = () => {
    setEditingLineItems(prev => deleteSelectedLines(prev))
  }

  const handleBulkEdit = (
    classVal: string,
    productCategoryVal: string,
    vendorVal: string,
    popStartDate: Date | null,
    popEndDate: Date | null,
  ) => {
    setEditingLineItems(prev => bulkEdit(prev, classVal, productCategoryVal, vendorVal, popStartDate, popEndDate))
    setSaveStatus('unsaved')
  }

  const handleBulkEditShippingAddress = (addressId: number) => {
    const address = shippingAddresses?.find(a => a.id === addressId)
    if (!address) return
    setEditingLineItems(prev =>
      bulkApplyShippingAddress(prev, {
        id: address.id,
        ShipToAttention: address.ShipToAttention,
        ShipToCompany: address.ShipToCompany,
        ShipToAddress1: address.ShipToAddress1,
        ShipToAddress2: address.ShipToAddress2,
        ShipToAddressCity: address.ShipToAddressCity,
        ShipToAddressState: address.ShipToAddressState,
        ShipToAddressZip: address.ShipToAddressZip,
        ShipToAddressCountry: address.ShipToAddressCountry,
        ReceiverContactName: address.ReceiverContactName,
        ReceiverContactEmail: address.ReceiverContactEmail,
        ReceiverContactPhone: address.ReceiverContactPhone,
      }),
    )
    setSaveStatus('unsaved')
  }

  const handleAddLineToKit = (kit: string) => {
    setEditingLineItems(prev => addSelectedLinesToKit(prev, kit))
    setSaveStatus('unsaved')
  }

  const handleZeroKitLineItems = (kit: string) => {
    setEditingLineItems(prev => zeroKitLinePrices(prev, kit))
    setKitHeaderAnchorEl(null)
    setSaveStatus('unsaved')
  }

  const handleCreateNewKit = (
    productDescription: string | null,
    mpn: string | null,
    quantity: number | null,
    unitPrice: number | null,
    extPrice: number | null,
    displayPartNumber: string | null,
  ) => {
    const kitId = uuidv4()
    const kitHeader = createBlankEditingLine(
      kitId,
      -1,
      productDescription,
      mpn,
      quantity,
      unitPrice,
      extPrice,
      displayPartNumber,
    )
    setEditingLineItems(prev => createNewKitWithSelection(prev, kitHeader))
    setSaveStatus('unsaved')
  }

  const handleUnkitLines = (kit: string) => {
    setKitHeaderAnchorEl(null)
    setEditingLineItems(prev => unkitLines(prev, kit))
    setKitCollapsed(prev => prev.filter(k => k !== kit))
    setUnkitStatus('saved')
    setSaveStatus('unsaved')
  }

  const handleReset = () => {
    setEditingLineItems(
      originalFlatLineItems.map(item => ({
        ...item,
        editing: false,
        addressId: null as number | null,
        selected: false,
      })),
    )
    setVitPrices(!!order.VITPrices)
    onReset()
    setSaveStatus('')
  }

  const handleSaveChanges = () => {
    setSaveStatus('saving')
    const lineItemsForApi = editingLineItems.map(
      ({ children: _children, editing: _editing, addressId: _addressId, selected: _selected, ...line }) => ({
        ...line,
        ProductDescription: (line.ProductDescription ?? '').substring(0, 4095),
        Notes: (line.Notes ?? '').substring(0, 4096) || null,
      }),
    )

    axios
      .patch(`/order/${orderID}/editlines`, {
        lineItems: lineItemsForApi,
        editedBy: user.email,
        logs: ['Line items updated'],
        vitPrices,
      })
      .then(res => {
        setLineItems(res.data)
        setOrder(prev => ({ ...prev, VITPrices: vitPrices }))
        setSaveStatus('saved')
        setOpen(false)
      })
      .catch(err => {
        setSaveStatus('error')
        if (err.response) {
          console.error(err.response)
        } else if (err.request) {
          console.error(err.request)
        } else {
          console.error(err.message)
        }
      })
  }

  const handleVitPricesChange = (checked: boolean) => {
    const factor = checked ? 0.99 : 1 / 0.99
    setEditingLineItems(prev =>
      prev.map(item => {
        if (item.Number === -1) return item

        const newUnitPrice = item.UnitPrice != null ? Math.round(item.UnitPrice * factor) : null
        if (newUnitPrice === null) return item
        return {
          ...item,
          UnitPrice: newUnitPrice,
          ExtPrice: newUnitPrice * (item.Quantity ?? 0),
        }
      }),
    )
    setVitPrices(checked)
    setSaveStatus('unsaved')
  }

  const getContraqerLineItems = useCallback(() => {
    setLineItemsLoading('loading')
    setSaveStatus('unsaved')
    setContraqerLineItemsDialogOpen(false)
    axios
      .get(`/contraqer/lineitems/${order.QuoteNumber}`)
      .then(res => {
        setEditingLineItems(
          flattenLineItems(res.data as lineItemType[]).map(item => ({
            ...item,
            editing: false,
            addressId: null as number | null,
            selected: false,
          })),
        )
        setLineItemsLoading('saved')
      })
      .catch(err => {
        console.error(err)
        setLineItemsLoading('error')
      })
  }, [order.QuoteNumber, setLineItems, setLineItemsLoading])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setVitPrices(!!order.VITPrices)
  }, [order.VITPrices])

  useEffect(() => {
    setEditingLineItems(
      flatLineItems.map(item => ({ ...item, editing: false, addressId: null as number | null, selected: false })),
    )
  }, [flatLineItems])

  return (
    <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
      <DialogTitle className="edit-lines-title">
        <div className="edit-lines-title-text">
          Edit Line Items <Chip label={<Typography>Quote # {quoteNumber}</Typography>} />
          <Chip
            label={
              <Typography>
                Order Total:{' '}
                {editingLineItems
                  .filter(item => item.Number !== -1)
                  .reduce((acc, item) => acc + (item.ExtPrice ?? 0), 0)
                  .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </Typography>
            }
          />
        </div>
        <div className="edit-lines-title-buttons">
          <FormControlLabel
            control={<Switch checked={vitPrices} onChange={e => handleVitPricesChange(e.target.checked)} />}
            label="VIT Prices"
          />
          <ButtonGroup variant="outlined">
            <Button onClick={() => setBulkExpand(true)}>Expand all</Button>
            <Button onClick={() => setBulkExpand(false)}>Collapse all</Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              variant="outlined"
              onClick={() => {
                setContraqerLineItemsDialogOpen(true)
              }}
              loading={lineItemsLoading === 'loading'}
            >
              <RefreshOutlined />
              Update Lines
            </Button>
            <Button variant="contained" onClick={handleReset} disabled={saveStatus === 'saving'}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={saveStatus === 'saving'}
              startIcon={saveStatus === 'saving' ? <CircularProgress size={16} /> : undefined}
            >
              {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
            </Button>
          </ButtonGroup>
          <IconButton
            onClick={() => (saveStatus === 'unsaved' ? setUnsavedChangesDialogOpen(true) : setOpen(false))}
            disabled={saveStatus === 'saving'}
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <Dialog open={contraqerLineItemsDialogOpen} onClose={() => setContraqerLineItemsDialogOpen(false)}>
        <DialogTitle>
          Update Lines{' '}
          <Typography variant="body2" color="text.secondary">
            Last CQ Backup: {new Date(cqBackup).toLocaleString()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Updating lines from Contraqer will overwrite any changes that have been made to the line items (from both
            Operations and Sales). <br /> <br />
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setContraqerLineItemsDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={getContraqerLineItems} loading={lineItemsLoading === 'loading'}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <div className="edit-lines-table-wrapper">
        <Table color="primary" stickyHeader className="edit-lines-table">
          <TableHead>
            <TableRow>
              <TableCell width={COLUMN_WIDTHS.Number} className="edit-lines-number-column">
                <Checkbox
                  checked={editingLineItems.every(item => item.selected)}
                  indeterminate={
                    editingLineItems.some(item => item.selected) && !editingLineItems.every(item => item.selected)
                  }
                  onChange={e =>
                    setEditingLineItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))
                  }
                />
                #
              </TableCell>
              <TableCell
                width={COLUMN_WIDTHS.More}
                className="edit-lines-more-column"
                sx={{ left: COLUMN_WIDTHS.Number }}
              />
              <TableCell
                width={COLUMN_WIDTHS.Supplier}
                className="edit-lines-supplier-column"
                sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More})` }}
              >
                Supplier
              </TableCell>
              <TableCell
                width={COLUMN_WIDTHS.NsVendor}
                className="edit-lines-ns-vendor-column"
                sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier})` }}
              >
                Netsuite Vendor
              </TableCell>
              <TableCell
                width={COLUMN_WIDTHS.DisplayPartNumber}
                className="edit-lines-display-part-number-column"
                sx={{
                  left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier} + ${COLUMN_WIDTHS.NsVendor})`,
                }}
              >
                Display Part #
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.Manufacturer}>Manufacturer</TableCell>
              <TableCell width={COLUMN_WIDTHS.MfrPartNumber}>Mfr Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.SupplierPartNumber}>Supplier Part #</TableCell>
              <TableCell width={COLUMN_WIDTHS.Description}>Description</TableCell>
              <TableCell width={COLUMN_WIDTHS.ShipToAddress}>Ship to Address</TableCell>
              <TableCell width={COLUMN_WIDTHS.Qty}>Qty</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitCost}>Unit Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtCost}>Ext Cost</TableCell>
              <TableCell width={COLUMN_WIDTHS.UnitPrice}>Unit Price</TableCell>
              <TableCell width={COLUMN_WIDTHS.ExtPrice}>Ext Price</TableCell>
              <TableCell width={COLUMN_WIDTHS.TAACompliant}>TAA Compliant</TableCell>
              <TableCell width={COLUMN_WIDTHS.ProductCategory}>Product Category</TableCell>
              <TableCell width={COLUMN_WIDTHS.ContractNumber}>Contract #</TableCell>
              <TableCell width={COLUMN_WIDTHS.PoPStart}>PoP Start Date</TableCell>
              <TableCell width={COLUMN_WIDTHS.PoPEnd}>PoP End Date</TableCell>
              {/*
              <TableCell width={COLUMN_WIDTHS.CountryOfOrigin}>Country of Origin</TableCell>
              <TableCell width={COLUMN_WIDTHS.ServiceTag}>Service Tag</TableCell>
              */}
            </TableRow>
          </TableHead>
          <TableBody>
            {editingLineItems.map((lineItem, rowIndex) => (
              <TableRow
                key={`${rowIndex}-${lineKey(lineItem)}`}
                className={
                  lineItem.Number === -1
                    ? 'kit-row'
                    : lineItem.Number ===
                      Math.max(
                        ...editingLineItems.filter(item => item.Kit === lineItem.Kit).map(item => item.Number ?? 0),
                      )
                    ? 'last-bundle-child'
                    : 'edit-lines-table-row'
                }
                sx={{
                  display:
                    lineItem.Number !== -1 &&
                    lineItem.Kit !== null &&
                    lineItem.Kit !== '' &&
                    kitCollapsed.includes(lineItem.Kit)
                      ? 'none'
                      : 'table-row',
                }}
              >
                {lineItem.Number !== -1 ? (
                  <TableCell className="edit-lines-number-column">
                    <Checkbox
                      checked={lineItem.selected}
                      onChange={e => {
                        const checked = e.target.checked
                        if ((e.nativeEvent as MouseEvent).shiftKey && lastCheckedIndexRef.current !== null) {
                          const from = Math.min(lastCheckedIndexRef.current, rowIndex)
                          const to = Math.max(lastCheckedIndexRef.current, rowIndex)
                          setEditingLineItems(prev =>
                            prev.map((item, i) =>
                              i >= from && i <= to && item.Number !== -1 ? { ...item, selected: checked } : item,
                            ),
                          )
                        } else {
                          setEditingLineItems(prev =>
                            prev.map(item =>
                              lineKey(item) === lineKey(lineItem) ? { ...item, selected: checked } : item,
                            ),
                          )
                        }
                        lastCheckedIndexRef.current = rowIndex
                      }}
                    />
                    {lineItem.Number}
                    {lineItem.Bundle && <Chip label={'Bundle'} color="primary" variant="filled" size="small" />}
                  </TableCell>
                ) : (
                  <TableCell className="edit-lines-number-column">
                    <Checkbox
                      checked={editingLineItems
                        .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                        .every(item => item.selected)}
                      indeterminate={
                        editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .some(item => item.selected) &&
                        !editingLineItems
                          .filter(item => item.Kit === lineItem.Kit && item.Number !== -1)
                          .every(item => item.selected)
                      }
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            item.Kit === lineItem.Kit && item.Number !== -1
                              ? { ...item, selected: e.target.checked }
                              : item,
                          ),
                        )
                      }
                    />
                    <IconButton
                      onClick={() =>
                        setKitCollapsed(prev =>
                          prev.includes(lineItem.Kit ?? '')
                            ? prev.filter(kit => kit !== lineItem.Kit)
                            : [...prev, lineItem.Kit ?? ''],
                        )
                      }
                    >
                      {kitCollapsed.includes(lineItem.Kit ?? '') ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    <Chip label={'Kit'} color="primary" variant="filled" size="small" />
                  </TableCell>
                )}
                <TableCell className="edit-lines-more-column" sx={{ left: COLUMN_WIDTHS.Number }}>
                  {lineItem.editing ? (
                    <IconButton
                      onClick={() =>
                        setEditingLineItems(prev =>
                          prev.map(item => (lineKey(item) === lineKey(lineItem) ? { ...item, editing: false } : item)),
                        )
                      }
                    >
                      <SaveOutlined />
                    </IconButton>
                  ) : lineItem.Number === -1 ? (
                    <IconButton
                      onClick={e =>
                        setKitHeaderAnchorEl({
                          kit: lineItem.Kit ?? '',
                          anchor: e.currentTarget,
                          lineKey: lineKey(lineItem),
                        })
                      }
                    >
                      <MoreVert />
                    </IconButton>
                  ) : (
                    <IconButton onClick={e => setMenuAnchorEl({ lineItem, anchor: e.currentTarget })}>
                      <MoreVert />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell
                  className="edit-lines-supplier-column"
                  sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More})` }}
                >
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Supplier"
                      value={lineItem.Supplier}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Supplier: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.supplier } }}
                    />
                  ) : (
                    lineItem.Supplier ?? '-'
                  )}
                </TableCell>
                <TableCell
                  className="edit-lines-ns-vendor-column"
                  sx={{ left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier})` }}
                >
                  {lineItem.editing ? (
                    <NetsuiteSearch
                      table="vendor"
                      value={lineItem.NetsuiteVendor}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, NetsuiteVendor: value } : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.NetsuiteVendor ?? '-'
                  )}
                </TableCell>
                <TableCell
                  className="edit-lines-display-part-number-column"
                  sx={{
                    left: `calc(${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.More} + ${COLUMN_WIDTHS.Supplier} + ${COLUMN_WIDTHS.NsVendor})`,
                  }}
                >
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Display Part #"
                      value={lineItem.DisplayPartNumber}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, DisplayPartNumber: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.displayPartNumber } }}
                    />
                  ) : (
                    lineItem.DisplayPartNumber ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Manufacturer"
                      value={lineItem.Manufacturer}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Manufacturer: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.manufacturer } }}
                    />
                  ) : (
                    lineItem.Manufacturer ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Mfr Part #"
                      value={lineItem.MfrPartNum}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, MfrPartNum: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.mfrPartNum } }}
                    />
                  ) : (
                    lineItem.MfrPartNum ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Supplier Part Number"
                      value={lineItem.SupplierPartNum}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, SupplierPartNum: e.target.value } : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.supplierPartNumber } }}
                    />
                  ) : (
                    lineItem.SupplierPartNum ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      multiline
                      label="Product Description"
                      value={lineItem.ProductDescription}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ProductDescription: e.target.value }
                              : item,
                          ),
                        )
                      }
                      slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.productDescription } }}
                    />
                  ) : (
                    <Expandable
                      key={lineKey(lineItem)}
                      text={lineItem.ProductDescription}
                      maxHeight={1}
                      expandButtonInline
                      bulkExpand={bulkExpand}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Ship to Address"
                      value={lineItem.addressId ?? defaultAddress?.id ?? null}
                      onChange={e => {
                        const address = shippingAddresses?.find(address => address.id === parseInt(e.target.value))
                        if (address) {
                          setEditingLineItems(prev =>
                            prev.map(item =>
                              lineKey(item) === lineKey(lineItem)
                                ? {
                                    ...item,
                                    ShipToAttention: address.ShipToAttention,
                                    ShipToCompany: address.ShipToCompany,
                                    ShipToAddress1: address.ShipToAddress1,
                                    ShipToAddress2: address.ShipToAddress2,
                                    ShipToAddressCity: address.ShipToAddressCity,
                                    ShipToAddressState: address.ShipToAddressState,
                                    ShipToAddressZip: address.ShipToAddressZip,
                                    ShipToAddressCountry: address.ShipToAddressCountry,
                                    addressId: address.id,
                                  }
                                : item,
                            ),
                          )
                        }
                      }}
                    >
                      {shippingAddresses?.map(address => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.ShipToAttention ?? ''} {address.ShipToCompany ?? ''} {address.ShipToAddress1 ?? ''}{' '}
                          {address.ShipToAddress2 ?? ''} {address.ShipToAddressCity ?? ''}{' '}
                          {address.ShipToAddressState ?? ''} {address.ShipToAddressZip ?? ''}{' '}
                          {address.ShipToAddressCountry ?? ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : lineItem.ShipToAddress1 ? (
                    <Expandable
                      text={`${lineItem.ShipToAttention ?? ''} ${lineItem.ShipToCompany ?? ''} ${
                        lineItem.ShipToAddress1
                      } ${lineItem.ShipToAddress2 ?? ''} ${lineItem.ShipToAddressCity ?? ''} ${
                        lineItem.ShipToAddressState ?? ''
                      } ${lineItem.ShipToAddressZip ?? ''} ${lineItem.ShipToAddressCountry ?? ''}`}
                      maxHeight={1}
                      expandButtonInline
                      bulkExpand={bulkExpand}
                    />
                  ) : (
                    <Expandable
                      text={`${defaultAddress?.ShipToAttention ?? ''} ${defaultAddress?.ShipToCompany ?? ''} ${
                        defaultAddress?.ShipToAddress1 ?? ''
                      } ${defaultAddress?.ShipToAddress2 ?? ''} ${defaultAddress?.ShipToAddressCity ?? ''} ${
                        defaultAddress?.ShipToAddressState ?? ''
                      } ${defaultAddress?.ShipToAddressZip ?? ''} ${defaultAddress?.ShipToAddressCountry ?? ''}`}
                      maxHeight={1}
                      expandButtonInline
                      bulkExpand={bulkExpand}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <NumberField
                      size="small"
                      label="Qty"
                      value={lineItem.Quantity}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'Quantity', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.Quantity ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Unit Cost"
                      value={lineItem.UnitCost ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'UnitCost', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.UnitCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Ext Cost"
                      value={lineItem.ExtCost ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'ExtCost', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ExtCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Unit Price"
                      value={lineItem.UnitPrice ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'UnitPrice', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.UnitPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <CurrencyInput
                      size="small"
                      label="Ext Price"
                      value={lineItem.ExtPrice ?? null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, ...updatePricing(item, 'ExtPrice', value) }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ExtPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="TAA Compliant"
                      value={lineItem.TaaCompliant ? 'Yes' : lineItem.TaaCompliant === false ? 'No' : 'N/A'}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, TaaCompliant: e.target.value === 'N/A' ? null : e.target.value === 'Yes' }
                              : item,
                          ),
                        )
                      }
                    >
                      <MenuItem value="N/A">Not Applicable</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                  ) : lineItem.TaaCompliant ? (
                    'Yes'
                  ) : lineItem.TaaCompliant === false ? (
                    'No'
                  ) : (
                    'Not Applicable'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Product Category"
                      value={lineItem.ProductCategory}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, ProductCategory: e.target.value } : item,
                          ),
                        )
                      }
                    >
                      {productTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    lineItem.ProductCategory ?? '-'
                  )}
                </TableCell>
                {/*
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Country of Origin"
                      value={lineItem.CountryOfOrigin}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, CountryOfOrigin: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.CountryOfOrigin ?? '-'
                  )}
                </TableCell>
                
                <TableCell>
                  {lineItem.editing ? (
                    <TextField
                      size="small"
                      label="Service Tag"
                      value={lineItem.ServiceTag}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, ServiceTag: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  ) : (
                    lineItem.ServiceTag ?? '-'
                  )}
                </TableCell>
                */}
                <TableCell width={COLUMN_WIDTHS.ContractNumber}>
                  {lineItem.editing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Contract #"
                      value={lineItem.Class}
                      onChange={e =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem) ? { ...item, Class: e.target.value } : item,
                          ),
                        )
                      }
                    >
                      {contractClassOptions.map(cls => (
                        <MenuItem key={cls.id} value={cls.name}>
                          {cls.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    lineItem.Class
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <DatePicker
                      size="small"
                      label="PoP Start Date"
                      value={lineItem.PopStartDate ? dayjs.utc(lineItem.PopStartDate) : null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, PopStartDate: value?.toDate() ?? null }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : lineItem.PopStartDate ? (
                    dayjs.utc(lineItem.PopStartDate).format('MM/DD/YYYY')
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {lineItem.editing ? (
                    <DatePicker
                      size="small"
                      label="PoP End Date"
                      value={lineItem.PopEndDate ? dayjs.utc(lineItem.PopEndDate) : null}
                      onChange={value =>
                        setEditingLineItems(prev =>
                          prev.map(item =>
                            lineKey(item) === lineKey(lineItem)
                              ? { ...item, PopEndDate: value?.toDate() ?? null }
                              : item,
                          ),
                        )
                      }
                    />
                  ) : lineItem.PopEndDate ? (
                    dayjs.utc(lineItem.PopEndDate).format('MM/DD/YYYY')
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <div className="add-new-line-fab-container">
            <Fab
              color="primary"
              variant="extended"
              size="large"
              onClick={() =>
                setEditingLineItems(prev => [
                  ...prev,
                  createBlankEditingLine(
                    null,
                    editingLineItems.filter(item => item.Number !== null && item.Number > 0).length + 1,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                  ),
                ])
              }
            >
              Add New Line <Add />
            </Fab>
          </div>
        </Table>
      </div>

      <Snackbar open={saveStatus === 'error'} onClose={() => setSaveStatus('')}>
        <Alert severity="error" variant="filled" onClose={() => setSaveStatus('')}>
          There was a problem saving the edits. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveStatus === 'saved'}
        onClose={() => setSaveStatus('')}
        message="Edits saved successfully"
        autoHideDuration={5000}
      />
      <Snackbar
        open={unkitStatus === 'saved'}
        onClose={() => setUnkitStatus('')}
        message="Lines unkitted successfully"
        autoHideDuration={5000}
      />
      <Snackbar open={lineItemsLoading === 'error'} onClose={() => setLineItemsLoading('')}>
        <Alert severity="error" variant="filled" onClose={() => setLineItemsLoading('')}>
          There was a problem updating the lines. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={lineItemsLoading === 'saved'}
        onClose={() => setLineItemsLoading('')}
        message="Lines updated successfully"
        autoHideDuration={5000}
      />

      {/* Normal line menu */}
      <Menu open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)} anchorEl={menuAnchorEl?.anchor}>
        <MenuItem onClick={() => menuAnchorEl && handleAddLineAfter(menuAnchorEl.lineItem)}>Add line</MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchorEl) {
              setEditingLineItems(prev =>
                prev.map(item =>
                  item.Number === menuAnchorEl.lineItem.Number && item.Kit === menuAnchorEl.lineItem.Kit
                    ? { ...item, editing: true }
                    : item,
                ),
              )
              setMenuAnchorEl(null)
            }
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => menuAnchorEl && handleDuplicateLineItem(menuAnchorEl.lineItem)}>Duplicate</MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchorEl) {
              setDeleteConfirm({ type: 'line', lineItem: menuAnchorEl.lineItem })
              setMenuAnchorEl(null)
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Kit header menu */}
      <Menu
        open={Boolean(kitHeaderAnchorEl)}
        onClose={() => setKitHeaderAnchorEl(null)}
        anchorEl={kitHeaderAnchorEl?.anchor}
      >
        <MenuItem
          onClick={() => {
            if (kitHeaderAnchorEl) {
              setEditingLineItems(prev =>
                prev.map(item => (lineKey(item) === kitHeaderAnchorEl.lineKey ? { ...item, editing: true } : item)),
              )
              setKitHeaderAnchorEl(null)
            }
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => kitHeaderAnchorEl && handleZeroKitLineItems(kitHeaderAnchorEl.kit)}>
          Zero kit line items
        </MenuItem>
        <MenuItem onClick={() => kitHeaderAnchorEl && handleUnkitLines(kitHeaderAnchorEl.kit)}>Unkit lines</MenuItem>
        <MenuItem
          onClick={() => {
            if (kitHeaderAnchorEl) {
              setDeleteConfirm({ type: 'kit', kit: kitHeaderAnchorEl.kit })
              setKitHeaderAnchorEl(null)
            }
          }}
        >
          Delete kit
        </MenuItem>
      </Menu>

      <Dialog
        open={saveStatus === 'unsaved' && unsavedChangesDialogOpen}
        onClose={() => setUnsavedChangesDialogOpen(false)}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to exit? Any unsaved changes will be lost.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setUnsavedChangesDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Exit without Saving
          </Button>
          <Button
            variant="contained"
            loading={saveStatus === 'saving'}
            onClick={() => {
              handleSaveChanges()
              setOpen(false)
            }}
          >
            Save and Exit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{deleteConfirm?.type === 'kit' ? 'Delete kit' : 'Delete line'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the selected {deleteConfirm?.type === 'kit' ? 'kit' : 'line items'}? This
            can be reverted with Reset until you save.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (deleteConfirm?.type === 'line') {
                handleDeleteLineItem(deleteConfirm.lineItem)
              } else if (deleteConfirm?.type === 'kit') {
                handleDeleteKit(deleteConfirm.kit)
              }
              setDeleteConfirm(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ActionBar
        headerPurchaseVehicle={order.PurchaseVehicle ?? ''}
        contractClassOptions={contractClassOptions}
        lineItems={editingLineItems}
        setLineItems={lineItems => setEditingLineItems(lineItems as editingLineItemType[])}
        totalCount={editingLineItems.length}
        selectedLineItems={editingLineItems.filter(item => item.selected)}
        setSelectedLineItems={lineItems =>
          setEditingLineItems(prev =>
            prev.map(item => (lineItems.includes(item) ? { ...item, selected: true } : { ...item, selected: false })),
          )
        }
        onCreateBundle={handleCreateBundle}
        onDeleteSelected={handleDeleteSelected}
        onBulkEdit={handleBulkEdit}
        onBulkEditShippingAddress={handleBulkEditShippingAddress}
        onAddLineToKit={handleAddLineToKit}
        onCreateNewKit={handleCreateNewKit}
        shippingAddresses={shippingAddresses ?? []}
      />
    </Dialog>
  )
}
